import { useEffect, useState, useCallback } from 'react';
import { walmartAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Loader2, Save, Send, RefreshCw, CheckCircle2, XCircle, AlertCircle,
  Eye, Copy, Check, ShoppingBag, ExternalLink, ImageIcon, Sparkles,
  ShieldCheck, Layers, Plus, Trash2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const FEED_STATUS_BADGE = {
  SUBMITTED: 'bg-amber-50 text-amber-700 border-amber-200',
  INPROGRESS: 'bg-blue-50 text-blue-700 border-blue-200',
  PROCESSED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  COMPLETE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  ERROR: 'bg-red-50 text-red-700 border-red-200',
  FAILED: 'bg-red-50 text-red-700 border-red-200',
  REJECTED: 'bg-red-50 text-red-700 border-red-200',
};

export default function WalmartListingPanel({ product }) {
  const productId = product.id;
  const [status, setStatus] = useState(null);
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [previewJson, setPreviewJson] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [checking, setChecking] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [check, setCheck] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([walmartAPI.status(), walmartAPI.get(productId)]);
      setStatus(s.data);
      setPack(p.data);
    } catch (err) {
      console.error('Walmart load failed', err);
      toast.error('Failed to load Walmart panel');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  // Actively re-poll Walmart while a feed is in flight. We call
  // refresh-status (NOT just the stored pack) so the status keeps advancing
  // even when the backend's in-memory poller has stopped — its 20-min cap,
  // a server restart, or a multi-worker deploy would otherwise leave the UI
  // sitting on "RECEIVED" forever.
  useEffect(() => {
    const fstatus = (pack?.feed?.status || '').toUpperCase();
    const term = ['PROCESSED', 'COMPLETE', 'ERROR', 'FAILED', 'REJECTED'];
    if (!pack?.feed?.id || term.includes(fstatus)) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        await walmartAPI.refreshStatus(productId);
      } catch (err) {
        // Transient Walmart 5xx/520 — ignore; the next tick retries.
      }
      if (!cancelled) await load();
    };
    tick(); // fire immediately so we don't wait a full interval on open
    const handle = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(handle); };
  }, [pack?.feed?.id, pack?.feed?.status, productId, load]);

  const setField = (key, val) => setPack((prev) => ({ ...(prev || {}), [key]: val }));

  const overridesFor = (extra = {}) => ({
    image_urls: Array.isArray(pack?.image_urls) ? pack.image_urls : [],
    image_base: (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, ''),
    ...extra,
  });

  const handleSave = async () => {
    if (!pack) return;
    try {
      setSaving(true);
      // Only send the editable fields
      const editable = {
        title: pack.title, short_description: pack.short_description,
        sku: pack.sku, brand: pack.brand, gtin: pack.gtin,
        price: Number(pack.price) || 0, quantity: Number(pack.quantity) || 0,
        ring_size: pack.ring_size, metal_type: pack.metal_type, metal_purity: pack.metal_purity,
        metal_color: pack.metal_color,
        center_stone_carat: Number(pack.center_stone_carat) || 0,
        center_stone_cut: pack.center_stone_cut, diamond_shape: pack.diamond_shape,
        shipping_weight_lb: Number(pack.shipping_weight_lb) || 0.1,
        fulfillment_lag_time: Number(pack.fulfillment_lag_time) || 1,
        lag_time_exempt: !!pack.lag_time_exempt,
        tax_code: pack.tax_code,
        ring_style: pack.ring_style, age_group: pack.age_group, gender: pack.gender,
        country_of_origin: pack.country_of_origin, color: pack.color,
        setting: pack.setting, is_resizable: pack.is_resizable,
        stone_creation: pack.stone_creation,
        number_of_diamonds: Number(pack.number_of_diamonds) || 1,
        number_of_prongs: Number(pack.number_of_prongs) || 4,
        key_features: pack.key_features,
        image_urls: Array.isArray(pack.image_urls) ? pack.image_urls : [],
        variants_enabled: !!pack.variants_enabled,
        variant_sizes: Array.isArray(pack.variant_sizes) ? pack.variant_sizes : [],
        variant_primary_size: pack.variant_primary_size,
        variant_metals: Array.isArray(pack.variant_metals) ? pack.variant_metals : [],
        variant_primary_metal: Number(pack.variant_primary_metal) || 0,
      };
      const res = await walmartAPI.patch(productId, editable);
      setPack(res.data);
      toast.success('Saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const runCheck = async (silent = false) => {
    try {
      setChecking(true);
      const res = await walmartAPI.preview(productId, overridesFor());
      setCheck(res.data);
      if (!silent) {
        const blocking = res.data.blocking_count || 0;
        if (blocking === 0) toast.success('Pre-flight clean — ready to push');
        else toast.error(`${blocking} issue${blocking === 1 ? '' : 's'} must be fixed before pushing`);
      }
      return res.data;
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.detail || 'Pre-flight check failed');
      return null;
    } finally {
      setChecking(false);
    }
  };

  const handleGenerateCopy = async () => {
    try {
      setGenerating(true);
      const res = await walmartAPI.generateCopy(productId, { save: true });
      setPack((prev) => ({
        ...(prev || {}),
        ...(res.data.pack || {}),
      }));
      const repaired = res.data.repaired_fields || [];
      if (res.data.ai_error) {
        toast.warning(`Used the built-in template (${res.data.ai_error.slice(0, 80)})`);
      } else if (repaired.length) {
        toast.warning(`Copy written, ${repaired.join(', ')} rebuilt from attributes to stay consistent`);
      } else {
        toast.success(`SEO copy written by ${res.data.model}`);
      }
      await runCheck(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Copy generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handlePreview = async () => {
    try {
      const res = await walmartAPI.preview(productId, overridesFor());
      setPreviewJson(res.data);
      setCheck(res.data);
      toast.success('Preview generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    }
  };

  const handlePublish = async () => {
    if (!Array.isArray(pack.image_urls) || pack.image_urls.length === 0) {
      toast.error('Pick at least one photo before publishing — Walmart requires a main image.');
      return;
    }
    if (!(Number(pack.price) > 0)) {
      toast.error('Set a Price above $0 and hit Save first — Walmart rejects $0 items ("Invalid Data").');
      return;
    }
    const pre = await runCheck(true);
    if (pre && (pre.blocking_count || 0) > 0) {
      toast.error(`${pre.blocking_count} content issue${pre.blocking_count === 1 ? '' : 's'} would get this rejected — see "Pre-flight" below`);
      return;
    }
    const count = pre?.item_count || 1;
    if (!confirm(`Publish "${pack.title}" to Walmart ${status?.env}?\n\n${count > 1 ? `${count} variants will be created on ONE item page.` : 'A single item will be created.'}`)) {
      return;
    }
    try {
      setPublishing(true);
      const res = await walmartAPI.publish(productId, overridesFor());
      toast.success(`Feed submitted: ${res.data.feed_id}${res.data.item_count > 1 ? ` (${res.data.item_count} variants)` : ''}`);
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : JSON.stringify(detail || 'Publish failed').slice(0, 400);
      toast.error(msg);
    } finally {
      setPublishing(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await walmartAPI.refreshStatus(productId);
      await load();
      toast.success('Status refreshed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const copy = (val, key) => {
    navigator.clipboard.writeText(val || '');
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  if (loading || !pack) {
    return <div className="flex justify-center items-center py-12"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>;
  }

  // Hard-block + helpful message if Walmart isn't configured at all (env vars missing)
  if (status && !status.configured) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-800">
        <div className="font-semibold mb-1">Walmart API not configured</div>
        Add <code className="px-1 py-0.5 bg-amber-100 rounded">WALMART_CLIENT_ID</code> + <code className="px-1 py-0.5 bg-amber-100 rounded">WALMART_CLIENT_SECRET</code> in your deployment env and redeploy.
      </div>
    );
  }

  const feed = pack.feed;
  const feedStatusUpper = (feed?.status || '').toUpperCase();
  const isInFlight = feed && !['PROCESSED', 'COMPLETE', 'ERROR', 'FAILED', 'REJECTED'].includes(feedStatusUpper);

  const descWords = (pack.short_description || '').trim()
    ? (pack.short_description || '').trim().split(/\s+/).length : 0;
  const issues = check?.validation_issues || [];
  const errors = issues.filter((i) => i.severity !== 'warning');
  const warnings = issues.filter((i) => i.severity === 'warning');
  const contentFields = ['Visible.productName', 'Visible.shortDescription', 'Visible.keyFeatures'];
  const isContent = (i) => contentFields.some((f) => (i.field || '').startsWith(f));
  const allSizes = pack.all_ring_sizes || [];
  const selectedSizes = Array.isArray(pack.variant_sizes) ? pack.variant_sizes : [];
  const variantMetals = Array.isArray(pack.variant_metals) ? pack.variant_metals : [];
  const variantCount = pack.variants_enabled
    ? Math.max(1, selectedSizes.length) * Math.max(1, variantMetals.length) : 1;

  const toggleSize = (size) => {
    const next = selectedSizes.includes(size)
      ? selectedSizes.filter((s) => s !== size)
      : [...selectedSizes, size].sort((a, b) => Number(a) - Number(b));
    setField('variant_sizes', next);
    if (!next.includes(pack.variant_primary_size)) setField('variant_primary_size', next[0] || '7');
  };

  const setMetalRow = (idx, key, val) => {
    const next = variantMetals.map((m, i) => (i === idx ? { ...m, [key]: val } : m));
    setField('variant_metals', next);
  };

  return (
    <div className="space-y-6 pb-8" data-testid="walmart-panel">
      {/* ===== Status banner ===== */}
      <div className={`rounded-xl border px-5 py-4 flex items-center justify-between gap-4 ${status?.token_ok ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'}`}>
        <div className="text-sm">
          <div className="font-semibold flex items-center gap-2">
            <ShoppingBag size={14} /> Walmart Seller API · {status?.env}
            {status?.token_ok
              ? <CheckCircle2 size={14} className="text-emerald-600" />
              : <XCircle size={14} className="text-red-600" />}
          </div>
          <div className="text-[11px] text-zinc-600 mt-0.5">
            {status?.token_ok
              ? `Connected to ${status.base_url}`
              : status?.token_error || 'Token fetch failed — check WALMART_CLIENT_ID / SECRET.'}
          </div>
        </div>
      </div>

      {/* ===== Feed status card (only after first publish) ===== */}
      {feed && (
        <div className="bg-white border border-zinc-200 rounded-xl p-5">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div>
              <h3 className="text-sm font-semibold text-zinc-900 flex items-center gap-1.5">
                Last submission
                <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold uppercase tracking-wider ${FEED_STATUS_BADGE[feedStatusUpper] || 'bg-zinc-50 text-zinc-600 border-zinc-200'}`}>
                  {feed.status || '—'}
                </span>
              </h3>
              <div className="text-[11px] text-zinc-500 mt-0.5">
                Feed ID: <code className="font-mono">{feed.id}</code> · submitted {feed.submitted_at?.slice(0, 19)}Z
              </div>
            </div>
            <Button
              onClick={handleRefresh}
              disabled={refreshing}
              size="sm"
              variant="outline"
              className="h-8 px-3 text-xs"
              data-testid="walmart-refresh-status"
            >
              {refreshing ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <RefreshCw size={12} className="mr-1.5" />}
              Refresh status
            </Button>
          </div>
          {isInFlight && (
            <div className="text-[11px] text-zinc-500 flex flex-col gap-0.5" data-testid="walmart-feed-polling">
              <span className="italic flex items-center gap-1.5">
                <Loader2 size={11} className="animate-spin" /> Checking Walmart every 30 seconds (auto)…
              </span>
              <span className="text-zinc-400">
                Walmart can take a few minutes (occasionally longer) to finish processing a new item. You can leave this page — the status is saved and keeps updating.
                {feed.last_polled_at ? ` Last checked ${feed.last_polled_at.slice(11, 19)}Z.` : ''}
              </span>
            </div>
          )}
          {feed.errors && feed.errors.length > 0 && (
            <div className="mt-3 space-y-2">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-red-600">Item errors ({feed.errors.length})</div>
              {feed.errors.slice(0, 6).map((e, i) => (
                <div key={i} className="bg-red-50 border border-red-100 rounded p-2 text-[11px] text-red-800 flex gap-2 items-start">
                  <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-mono">{e.code || 'ERR'} · {e.field || ''}</div>
                    <div>{e.message}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== Listing form ===== */}
      <div className="bg-white border border-zinc-200 rounded-xl p-5">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-zinc-900">Walmart listing</h3>
            <p className="text-[11px] text-zinc-500">Fields below are mapped to Walmart&apos;s MP_ITEM schema (Fine Rings → Engagement). Required: title, brand, price, ≥1 image.</p>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Button onClick={handleGenerateCopy} disabled={generating} variant="outline" size="sm" className="h-8 px-3 text-xs border-violet-200 text-violet-700 hover:bg-violet-50" data-testid="walmart-generate-copy-btn">
              {generating ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Sparkles size={12} className="mr-1.5" />}
              Generate SEO copy
            </Button>
            <Button onClick={() => runCheck(false)} disabled={checking} variant="outline" size="sm" className="h-8 px-3 text-xs" data-testid="walmart-preflight-btn">
              {checking ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <ShieldCheck size={12} className="mr-1.5" />}
              Pre-flight check
            </Button>
            <Button onClick={handlePreview} variant="outline" size="sm" className="h-8 px-3 text-xs" data-testid="walmart-preview-btn">
              <Eye size={12} className="mr-1.5" /> Preview JSON
            </Button>
            <Button onClick={handleSave} disabled={saving} variant="outline" size="sm" className="h-8 px-3 text-xs" data-testid="walmart-save-btn">
              {saving ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Save size={12} className="mr-1.5" />}
              Save draft
            </Button>
            <Button
              onClick={handlePublish}
              disabled={publishing || !status?.token_ok}
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3 text-xs"
              data-testid="walmart-publish-btn"
              title={!status?.token_ok ? 'Walmart API not authenticated' : ''}
            >
              {publishing ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Send size={12} className="mr-1.5" />}
              {variantCount > 1 ? `Push ${variantCount} variants` : 'Push to Walmart'}
            </Button>
          </div>
        </div>

        {/* ===== Pre-flight panel ===== */}
        {check && (
          <div
            className={`mb-4 rounded-lg border px-4 py-3 ${errors.length ? 'bg-rose-50 border-rose-200' : 'bg-emerald-50 border-emerald-200'}`}
            data-testid="walmart-preflight-panel"
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[12px] font-semibold flex items-center gap-1.5">
                {errors.length ? <XCircle size={13} className="text-rose-600" /> : <CheckCircle2 size={13} className="text-emerald-600" />}
                <span className={errors.length ? 'text-rose-800' : 'text-emerald-800'} data-testid="walmart-preflight-summary">
                  {errors.length
                    ? `${errors.length} blocking issue${errors.length === 1 ? '' : 's'} — Walmart would reject this`
                    : 'Pre-flight clean — attributes, copy and variants all agree'}
                </span>
              </div>
              <span className="text-[11px] text-zinc-500 font-mono">
                {check.item_count || 1} item{(check.item_count || 1) === 1 ? '' : 's'} in feed
                {check.specs ? ` · ${Number(check.specs.carat).toFixed(2)}ct ${check.specs.shape} · ${check.specs.color}/${check.specs.clarity} · ${check.specs.metal_label}` : ''}
              </span>
            </div>
            {(errors.length > 0 || warnings.length > 0) && (
              <div className="mt-2 space-y-1.5 max-h-72 overflow-auto">
                {[...errors, ...warnings].slice(0, 25).map((i, idx) => (
                  <div
                    key={`${i.field}-${idx}`}
                    className={`text-[11px] rounded px-2 py-1.5 border flex gap-2 items-start ${i.severity === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-white border-rose-200 text-rose-800'}`}
                    data-testid={`walmart-issue-${idx}`}
                  >
                    <AlertCircle size={11} className="mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-mono text-[10px] opacity-70">{i.field}</span>
                      {isContent(i) && (
                        <span className="ml-1.5 text-[9px] uppercase tracking-wider font-semibold bg-rose-100 text-rose-700 px-1 py-0.5 rounded">
                          content standards
                        </span>
                      )}
                      <div>{i.message}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {errors.some(isContent) && (
              <button
                onClick={handleGenerateCopy}
                className="mt-2 text-[11px] font-semibold text-violet-700 underline hover:text-violet-900"
                data-testid="walmart-fix-copy-link"
              >
                Rewrite the copy from these attributes and clear the contradictions
              </button>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={`Title (${(pack.title || '').length}/90 for SEO, 100 max)`} value={pack.title || ''} onChange={(v) => setField('title', v)} testid="walmart-title" hint={(pack.title || '').length > 90 ? '⚠ Over 90 chars hurts Walmart SEO — hit "Generate SEO copy"' : undefined} />
          <Field label="Brand" value={pack.brand || ''} onChange={(v) => setField('brand', v)} testid="walmart-brand" />
          <Field label="SKU" value={pack.sku || ''} onChange={(v) => setField('sku', v)} testid="walmart-sku" hint="Your unique stock-keeping ID" />
          <Field label="GTIN / UPC (optional — exemption applies)" value={pack.gtin || ''} onChange={(v) => setField('gtin', v)} testid="walmart-gtin" hint="Leave blank to use your approved GTIN exemption (sends Walmart's 'CUSTOM' placeholder). Enter a real 14-digit GTIN or 12-digit UPC only if the piece has one." />
          <Field label="Price (USD)" value={pack.price} onChange={(v) => setField('price', v)} testid="walmart-price" type="number" hint={!(Number(pack.price) > 0)
            ? (pack.matrix_price > 0
              ? `Leave blank to use the matrix price $${Number(pack.matrix_price).toLocaleString()} (${pack.metal_purity || '14k'}, incl. Walmart's jewelry fee)`
              : '⚠ Required — Walmart rejects $0 items ("Invalid Data")')
            : undefined} />
          <Field label="Quantity" value={pack.quantity} onChange={(v) => setField('quantity', v)} testid="walmart-quantity" type="number" />
        </div>

        <div className="mt-3">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">
              Site description <span className="normal-case font-normal tracking-normal text-zinc-400">(Walmart&apos;s only description field — shown as &quot;About this item&quot;)</span>
            </Label>
            <span className={`text-[10px] font-mono ${descWords > 200 || (descWords > 0 && descWords < 60) ? 'text-rose-600' : 'text-zinc-400'}`} data-testid="walmart-desc-wordcount">
              {descWords} words {descWords ? '' : '(auto-generated on push)'}
            </span>
          </div>
          <textarea
            value={pack.short_description || ''}
            onChange={(e) => setField('short_description', e.target.value)}
            rows={6}
            className="mt-1 w-full text-[13px] bg-white border border-zinc-200 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-emerald-200"
            placeholder="Leave blank to auto-build a Walmart-compliant description from the attributes below, or hit “Generate SEO copy”."
            data-testid="walmart-short-description"
          />
          <div className="text-[10px] text-zinc-400 mt-1">
            Plain paragraphs only, 60–200 words. Bullets, markdown, shipping/returns promises, store or contact details and &quot;available on request&quot; placeholders are stripped automatically — Walmart rejects them.
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-zinc-100">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600 mb-2">
            Jewelry attributes
            <span className="ml-2 text-[10px] font-normal text-zinc-400 normal-case tracking-normal">
              auto-derived from your approved title — edit to override
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Ring size (single item)" value={pack.ring_size || ''} onChange={(v) => setField('ring_size', v)} testid="walmart-ring-size" hint={pack.variants_enabled ? 'Ignored — variants publish the whole size run' : undefined} />
            <Field label="Metal purity" value={pack.metal_purity || ''} onChange={(v) => setField('metal_purity', v)} testid="walmart-metal-purity" hint="10k / 14k / 18k / platinum / silver" />
            <Field label="Metal type" value={pack.metal_type || ''} onChange={(v) => setField('metal_type', v)} testid="walmart-metal-type" hint="Yellow / White / Rose Gold, Platinum, Sterling Silver" />
            <Field label="Center stone (ct)" value={pack.center_stone_carat} onChange={(v) => setField('center_stone_carat', v)} testid="walmart-center-carat" type="number" />
            <ReadOnlyField label="Color" value={pack.center_stone_color || 'E'} testid="walmart-center-color" hint="Store standard — fixed on every TheLocalJewel center stone" />
            <ReadOnlyField label="Clarity" value={pack.center_stone_clarity || 'VVS2'} testid="walmart-center-clarity" hint="Store standard — all copy is written to match" />
            <Field label="Cut grade" value={pack.center_stone_cut || ''} onChange={(v) => setField('center_stone_cut', v)} testid="walmart-center-cut" />
            <Field label="Tax code" value={pack.tax_code || ''} onChange={(v) => setField('tax_code', v)} testid="walmart-tax-code" hint="2038710 = fine jewelry" />
          </div>
        </div>

        {/* ===== Variant group ===== */}
        <div className="mt-4 pt-4 border-t border-zinc-100" data-testid="walmart-variants-card">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={!!pack.variants_enabled}
              onChange={(e) => setField('variants_enabled', e.target.checked)}
              data-testid="walmart-variants-enabled"
            />
            <span className="text-[12px] text-zinc-700">
              <span className="font-semibold inline-flex items-center gap-1.5">
                <Layers size={12} /> Publish as a variant group
              </span>
              <span className="block text-[11px] text-zinc-500">
                One Walmart item page with a ring-size picker instead of isolated single-size listings. Ranks and converts far better, and every variant shares this copy.
              </span>
            </span>
          </label>

          {pack.variants_enabled && (
            <div className="mt-3 space-y-3">
              <div>
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">Ring sizes</Label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setField('variant_sizes', [...allSizes])}
                      className="text-[10px] text-zinc-500 underline hover:text-zinc-800"
                      data-testid="walmart-sizes-select-all"
                    >
                      all half sizes 3–12
                    </button>
                    <span className="text-[10px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-1.5 py-0.5" data-testid="walmart-variant-count">
                      {variantCount} variants
                    </span>
                  </div>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="walmart-size-chips">
                  {allSizes.map((size) => {
                    const on = selectedSizes.includes(size);
                    return (
                      <button
                        key={size}
                        type="button"
                        onClick={() => toggleSize(size)}
                        className={`h-7 min-w-[40px] px-2 rounded-md border text-[11px] font-mono transition ${on ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400'}`}
                        data-testid={`walmart-size-chip-${size}`}
                      >
                        {size}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <SelectField
                  label="Primary variant (shown by default)"
                  value={pack.variant_primary_size || '7'}
                  onChange={(v) => setField('variant_primary_size', v)}
                  options={selectedSizes.length ? selectedSizes : allSizes}
                  testid="walmart-variant-primary-size"
                  hint="Walmart allows exactly one"
                />
                <ReadOnlyField
                  label="Variant group ID"
                  value={pack.variant_group_id || `TLJ${productId.slice(0, 8).toUpperCase()}`}
                  testid="walmart-variant-group-id"
                  hint="Shared by every child item"
                />
                <ReadOnlyField
                  label="SKU pattern"
                  value={`${pack.sku || 'SKU'}-SZ75`}
                  testid="walmart-variant-sku-pattern"
                  hint="Size x10 suffix (SZ75 = size 7.5)"
                />
              </div>

              <div>
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">
                    Metal options <span className="normal-case font-normal tracking-normal text-zinc-400">(optional second axis — each metal can carry its own price)</span>
                  </Label>
                  <button
                    type="button"
                    onClick={() => setField('variant_metals', [
                      ...variantMetals,
                      {
                        metal_purity: pack.metal_purity || '14k',
                        metal_color: pack.metal_color || 'White Gold',
                        price: Number(pack.price)
                          || Number((pack.matrix_prices_by_metal || {})[pack.metal_purity || '14k'])
                          || 0,
                      },
                    ])}
                    className="text-[10px] text-zinc-600 underline hover:text-zinc-900 inline-flex items-center gap-1"
                    data-testid="walmart-add-metal-row"
                  >
                    <Plus size={10} /> add metal
                  </button>
                </div>
                {variantMetals.length === 0 ? (
                  <div className="mt-1 text-[11px] text-zinc-400">
                    No extra metals — every variant uses {pack.metal_label || `${pack.metal_purity} ${pack.metal_color}`} at $
                    {(Number(pack.price) || Number(pack.matrix_price) || 0).toLocaleString()}.
                    {pack.matrix_prices_by_metal && (
                      <span className="block mt-0.5 text-zinc-400">
                        Matrix: {Object.entries(pack.matrix_prices_by_metal)
                          .map(([k, v]) => `${k} $${Number(v).toLocaleString()}`).join(' · ')}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="mt-1.5 space-y-1.5">
                    {variantMetals.map((m, idx) => (
                      <div key={idx} className="flex items-center gap-2" data-testid={`walmart-metal-row-${idx}`}>
                        <select
                          value={m.metal_purity || '14k'}
                          onChange={(e) => setMetalRow(idx, 'metal_purity', e.target.value)}
                          className="h-8 text-[12px] bg-white border border-zinc-200 rounded px-2"
                          data-testid={`walmart-metal-purity-${idx}`}
                        >
                          {['silver', '10k', '14k', '18k', 'platinum'].map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                        <select
                          value={m.metal_color || 'White Gold'}
                          onChange={(e) => setMetalRow(idx, 'metal_color', e.target.value)}
                          className="h-8 text-[12px] bg-white border border-zinc-200 rounded px-2"
                          data-testid={`walmart-metal-color-${idx}`}
                        >
                          {['White Gold', 'Yellow Gold', 'Rose Gold', 'Platinum', 'Sterling Silver'].map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                        <Input
                          type="number"
                          value={m.price ?? ''}
                          onChange={(e) => setMetalRow(idx, 'price', Number(e.target.value))}
                          className="h-8 w-28 text-[12px]"
                          placeholder={`$${Number((pack.matrix_prices_by_metal || {})[m.metal_purity || '14k'] || 0).toLocaleString()}`}
                          data-testid={`walmart-metal-price-${idx}`}
                        />
                        <label className="text-[11px] text-zinc-500 inline-flex items-center gap-1">
                          <input
                            type="radio"
                            checked={Number(pack.variant_primary_metal || 0) === idx}
                            onChange={() => setField('variant_primary_metal', idx)}
                            data-testid={`walmart-metal-primary-${idx}`}
                          />
                          primary
                        </label>
                        <button
                          type="button"
                          onClick={() => setField('variant_metals', variantMetals.filter((_, i) => i !== idx))}
                          className="text-zinc-400 hover:text-rose-600"
                          data-testid={`walmart-metal-remove-${idx}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-zinc-100">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600 mb-2">
            Walmart-required attributes
            <span className="ml-2 text-[10px] font-normal text-zinc-400 normal-case tracking-normal">
              (closed-list values — Walmart will reject anything else)
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SelectField
              label="Ring style"
              value={pack.ring_style || 'Solitaire'}
              onChange={(v) => setField('ring_style', v)}
              testid="walmart-ring-style"
              options={['Solitaire', 'Multi-Stone', 'Cluster', 'Band', 'Eternity', 'Semi-Eternity', 'Filigree', 'Composite', 'Cocktail', 'Delicate', 'Bypass', 'Crossover', 'Open', 'Midi', 'Stacking', 'Signet']}
              hint="Engagement is NOT valid — pick the closest match"
            />
            <SelectField
              label="Age group"
              value={pack.age_group || 'Adult'}
              onChange={(v) => setField('age_group', v)}
              testid="walmart-age-group"
              options={['Adult', 'Teen', 'Child', 'Toddler', 'Infant', 'Baby']}
            />
            <SelectField
              label="Gender"
              value={pack.gender || 'Female'}
              onChange={(v) => setField('gender', v)}
              testid="walmart-gender"
              options={['Female', 'Male', 'Unisex']}
            />
            <SelectField
              label="Country of origin"
              value={pack.country_of_origin || 'United States'}
              onChange={(v) => setField('country_of_origin', v)}
              testid="walmart-country-of-origin"
              options={['United States', 'Canada', 'India', 'United Kingdom', 'Italy', 'France', 'Germany', 'China', 'Thailand', 'Japan', 'Israel', 'Belgium']}
              hint="Where substantial transformation took place"
            />
            <SelectField
              label="Metal color"
              value={pack.metal_color || 'Yellow Gold'}
              onChange={(v) => setField('metal_color', v)}
              testid="walmart-metal-color"
              options={['Yellow Gold', 'White Gold', 'Rose Gold', 'Platinum', 'Sterling Silver', 'Silver']}
              hint="Drives the metal + color attributes"
            />
            <SelectField
              label="Setting"
              value={pack.setting || 'Prongs'}
              onChange={(v) => setField('setting', v)}
              testid="walmart-setting"
              options={['Prongs', 'Bezel', 'Half Bezel', 'Pavé', 'Channel', 'Tension Mount', 'Cathedral', 'Illusion', 'Halo', 'Hidden Halo', 'Side Stone', 'Shared Prong', 'V-Prong', 'Trellis', 'Semi-Mount']}
            />
            <SelectField
              label="Diamond shape"
              value={pack.diamond_shape || 'Round'}
              onChange={(v) => setField('diamond_shape', v)}
              testid="walmart-diamond-shape"
              options={['Round', 'Princess', 'Emerald', 'Asscher', 'Marquise', 'Oval', 'Radiant', 'Pear', 'Heart', 'Cushion']}
              hint="Stone outline (separate from cut grade)"
            />
            <SelectField
              label="Cut grade"
              value={pack.center_stone_cut || 'Excellent'}
              onChange={(v) => setField('center_stone_cut', v)}
              testid="walmart-cut-grade"
              options={['Excellent', 'Very Good', 'Good', 'Fair', 'Poor']}
              hint="Quality grade (diamondCut field)"
            />
            <SelectField
              label="Stone creation"
              value={pack.stone_creation || 'Lab-grown'}
              onChange={(v) => setField('stone_creation', v)}
              testid="walmart-stone-creation"
              options={['Lab-grown', 'Natural']}
            />
            <SelectField
              label="Resizable"
              value={pack.is_resizable || 'Yes'}
              onChange={(v) => setField('is_resizable', v)}
              testid="walmart-is-resizable"
              options={['Yes', 'No']}
            />
            <SelectField
              label="Number of prongs"
              value={String(pack.number_of_prongs || 4)}
              onChange={(v) => setField('number_of_prongs', Number(v))}
              testid="walmart-number-of-prongs"
              options={['4', '6', '8']}
              hint="4 = classic / 6 = Tiffany / 8 = heavy protection"
            />
            <Field
              label="Total stones"
              value={pack.number_of_diamonds || 1}
              onChange={(v) => setField('number_of_diamonds', v)}
              testid="walmart-number-of-diamonds"
              type="number"
              hint="Diamonds on the ring (center + side)"
            />
            <Field
              label="Display color"
              value={pack.color || ''}
              onChange={(v) => setField('color', v)}
              testid="walmart-display-color"
              hint="Optional override — auto-derived from metal color"
            />
          </div>

          <div className="mt-3">
            <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">
              Key features <span className="text-rose-600 normal-case font-normal tracking-normal">(Walmart requires ≥ 5 entries, max 80 chars each)</span>
            </Label>
            <textarea
              value={pack.key_features || ''}
              onChange={(e) => setField('key_features', e.target.value)}
              rows={5}
              placeholder={'• 1.50ct round lab-grown diamond center\n• 14K white gold mounting\n• Prong setting hand-crafted in NYC\n• F color, VS1 clarity\n• Excellent cut grade — IGI/GIA certified'}
              className="mt-1 w-full text-[13px] bg-white border border-zinc-200 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-emerald-200 font-mono"
              data-testid="walmart-key-features"
            />
            <div className="text-[10px] text-zinc-400 mt-1">
              One bullet per line — auto-derived from product specs if left blank.
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-zinc-100" data-testid="walmart-image-selector">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600 inline-flex items-center gap-1.5">
                <ImageIcon size={11} /> Listing photos
              </div>
              <div className="text-[11px] text-zinc-500 mt-0.5">
                Pick the photos to push to Walmart. The first selected becomes the main image. Walmart needs at least 1.
              </div>
            </div>
            <span
              className="text-[11px] font-mono px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-100"
              data-testid="walmart-image-count"
            >
              {(pack.image_urls || []).length} selected
            </span>
          </div>
          {(pack.available_images || []).length === 0 ? (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
              No product photos found. Upload images on the Etsy/Media tab first — they&apos;ll appear here automatically.
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="walmart-image-grid">
              {(pack.available_images || []).map((img) => {
                const selectedIdx = (pack.image_urls || []).indexOf(img.url);
                const isSel = selectedIdx >= 0;
                return (
                  <button
                    key={img.id || img.url}
                    type="button"
                    onClick={() => {
                      const current = pack.image_urls || [];
                      const next = isSel
                        ? current.filter((u) => u !== img.url)
                        : [...current, img.url];
                      setField('image_urls', next);
                    }}
                    className={`relative aspect-square rounded overflow-hidden border-2 transition focus:outline-none ${
                      isSel
                        ? 'border-emerald-500 ring-2 ring-emerald-200'
                        : 'border-zinc-200 hover:border-zinc-400'
                    }`}
                    title={img.label || ''}
                    data-testid={`walmart-image-thumb-${img.id || img.url}`}
                  >
                    <img
                      src={img.url}
                      alt={img.label || 'product'}
                      className="w-full h-full object-cover bg-zinc-100"
                      loading="lazy"
                    />
                    {isSel && (
                      <span className="absolute top-1 right-1 bg-emerald-500 text-white text-[10px] font-bold w-5 h-5 rounded-full inline-flex items-center justify-center">
                        {selectedIdx + 1}
                      </span>
                    )}
                    {isSel && selectedIdx === 0 && (
                      <span className="absolute bottom-1 left-1 bg-white/95 text-emerald-700 text-[9px] font-semibold px-1.5 py-0.5 rounded">
                        MAIN
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-zinc-100">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600 mb-2">Shipping & fulfillment</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Field label="Weight (lb)" value={pack.shipping_weight_lb} onChange={(v) => setField('shipping_weight_lb', v)} testid="walmart-weight" type="number" step="0.01" />
            <Field label="Fulfillment lag (days)" value={pack.fulfillment_lag_time} onChange={(v) => setField('fulfillment_lag_time', v)} testid="walmart-lag" type="number" />
          </div>
          <label className="mt-3 flex items-start gap-2 text-[11px] text-zinc-600 cursor-pointer" data-testid="walmart-lag-exempt-label">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={!!pack.lag_time_exempt}
              onChange={(e) => setField('lag_time_exempt', e.target.checked)}
              data-testid="walmart-lag-exempt"
            />
            <span>
              I have an <strong>approved lag-time exemption</strong> from Walmart for this product type.
              <span className="block text-zinc-400">Walmart caps lag at <strong>1 day</strong> by default. Leave this unchecked unless your exemption is approved — anything above 1 day is otherwise auto-set to 1 to avoid a rejection.</span>
            </span>
          </label>
        </div>
      </div>

      {/* ===== Preview JSON modal ===== */}
      <Dialog open={!!previewJson} onOpenChange={() => setPreviewJson(null)}>
        <DialogContent className="sm:max-w-3xl bg-white">
          <DialogHeader>
            <DialogTitle>Walmart MP_ITEM payload preview</DialogTitle>
          </DialogHeader>
          {previewJson && (
            <>
              <div className="text-[11px] text-zinc-500 mb-2">Summary</div>
              <div className="bg-zinc-50 rounded p-3 text-[12px] font-mono grid grid-cols-2 gap-x-3 gap-y-1">
                {Object.entries(previewJson.summary || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2 col-span-1">
                    <span className="text-zinc-500">{k}</span>
                    <span className="text-zinc-900 truncate">{String(v)}</span>
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-zinc-500 mt-3 mb-1 flex justify-between items-center">
                <span>Full MP_ITEM JSON (will be POSTed to /v3/feeds?feedType=MP_ITEM)</span>
                <button
                  onClick={() => copy(JSON.stringify(previewJson.payload, null, 2), 'json')}
                  className="inline-flex items-center gap-1 text-zinc-500 hover:text-zinc-900"
                >
                  {copiedKey === 'json' ? <Check size={11} className="text-emerald-600" /> : <Copy size={11} />} Copy
                </button>
              </div>
              <pre className="bg-zinc-900 text-emerald-300 text-[10px] font-mono rounded p-3 overflow-auto max-h-[55vh] leading-snug">
                {JSON.stringify(previewJson.payload, null, 2)}
              </pre>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function Field({ label, value, onChange, testid, type = 'text', hint, step }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">{label}</Label>
      <Input
        type={type}
        step={step}
        value={value ?? ''}
        onChange={(e) => onChange(type === 'number' ? e.target.value : e.target.value)}
        className="mt-1 h-9 text-[13px]"
        data-testid={testid}
      />
      {hint && <div className="text-[10px] text-zinc-400 mt-1">{hint}</div>}
    </div>
  );
}

function ReadOnlyField({ label, value, testid, hint }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">{label}</Label>
      <div
        className="mt-1 h-9 flex items-center px-3 text-[13px] bg-zinc-50 border border-zinc-200 rounded-md text-zinc-700 font-mono truncate"
        data-testid={testid}
        title={String(value ?? '')}
      >
        {value ?? ''}
      </div>
      {hint && <div className="text-[10px] text-zinc-400 mt-1">{hint}</div>}
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid, hint }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">{label}</Label>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 h-9 w-full text-[13px] bg-white border border-zinc-200 rounded-md px-2 focus:outline-none focus:ring-2 focus:ring-emerald-200"
        data-testid={testid}
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
      {hint && <div className="text-[10px] text-zinc-400 mt-1">{hint}</div>}
    </div>
  );
}
